/**
 * Session-degraded detection + recovery for stale auth / App Check tokens.
 *
 * Failure mode this catches: a tab sits idle for hours (laptop closed
 * overnight, tab in the background), and auth / App Check tokens have a
 * short (~1h) TTL that needs refreshing. Background tabs get aggressively
 * throttled by Chrome and the refresh attempts often fail, especially if
 * reCAPTCHA risk-scores the long-idle then sudden-active pattern as
 * bot-like. After enough failures the App Check SDK enters a 24-hour
 * client-side throttle to protect reCAPTCHA's quota. Once that happens,
 * every Firestore read AND write fails with permission-denied and the app
 * looks broken with no signal to the user about what went wrong.
 *
 * Recovery is just a reload — the throttle state is in-memory only. We
 * watch for two distinctive signals:
 *
 *   1. The console.warn from `@firebase/app-check` containing
 *      "appCheck/throttled" — fires once when the throttle activates.
 *      Nothing can be salvaged from this state, so it degrades the
 *      session immediately.
 *   2. Firestore errors with `code === 'permission-denied'` that happen
 *      after the page has been open for >5 min — much more likely to be
 *      token expiry than an actual rules issue for an established
 *      session. A denial does NOT raise the banner by itself. It starts
 *      one VERIFICATION: re-mint the tokens, then read the user's own
 *      `users/{uid}` doc from the server with them. Only a fresh-token
 *      read that is itself refused degrades the session; a read that
 *      succeeds means the session is fine (the denial was a transient
 *      stale-token blip, or a genuine rules "no" for that one request)
 *      and the listeners are told to reconnect.
 *
 * The earlier design was "one silent refresh, then the banner on the next
 * denial". That raised the banner on healthy sessions three ways: sibling
 * listeners report the same denial milliseconds apart, so the "next
 * denial" arrived before the refresh had even returned; the one shot was
 * only re-armed by a listener recovering, so a long-lived tab spent it
 * once and never got it back; and a banner raised by a refused WRITE had
 * nothing to clear it when no listener had failed. Verification fixes all
 * three: concurrent denials join the same check, the check is re-armed by
 * time, and the banner clears whenever the session proves healthy — a
 * passing re-check (on a timer, and when the app returns to the
 * foreground), a recovered listener, or a write the server accepts.
 *
 * <SessionDegradedBanner> renders a persistent Reload bar for as long as
 * the session is degraded.
 *
 * Deliberately dependency-light: no firebase import at module scope (the
 * auth / firestore handles are pulled in with a dynamic import only when
 * a verification is actually run) and no import of sharedSubscription,
 * which imports *this* module's recovery notifier.
 */
import { logger } from './logger';

export type SessionDegradedReason = 'permission' | 'throttle';

export interface SessionDegradedState {
  degraded: boolean;
  reason: SessionDegradedReason | null;
}

/** Copy shown when a write is rejected because the session went stale. */
export const SESSION_STALE_WRITE_MESSAGE =
  "Couldn't save just now — the app is refreshing your connection. Please try again.";

/**
 * Copy for the late-failure path, where we've ALREADY refreshed the
 * session on the user's behalf and the retry still didn't land. "Reload
 * and try again" would be a lie there — the reload has effectively
 * happened — so this says what's true: the save is gone, the session
 * isn't, try the action again.
 */
export const SESSION_REFRESHED_RETRY_MESSAGE =
  "Your session had expired, so that save didn't go through. It's been refreshed — please try again.";

/**
 * Copy for a write the server refused even with a freshly minted token —
 * not a session problem, so don't send the user off to reload.
 */
export const WRITE_REFUSED_MESSAGE =
  "That save was refused — this item may be archived or locked for your role. It hasn't been saved; reload the page to see its current state.";

const pageLoadedAt = Date.now();

// Don't fire the permission-denied trigger in the first 5 minutes —
// those are real rules issues (a brand-new sign-in failing rules is not
// "stale token", and we'd rather not mask a true permission problem
// behind a "reload and try again" banner).
const PERMISSION_DENIED_GRACE_MS = 5 * 60 * 1000;

const APP_CHECK_THROTTLE_PATTERNS = [
  /appCheck\/throttled/i,
  /AppCheck.*Requests throttled/i,
];

const isAppCheckThrottleMessage = (msg: string | undefined): boolean => {
  if (!msg) return false;
  return APP_CHECK_THROTTLE_PATTERNS.some((p) => p.test(msg));
};

/* ------------------------------------------------------------------ */
/* Degraded state + subscription                                       */
/* ------------------------------------------------------------------ */

let state: SessionDegradedState = { degraded: false, reason: null };
const listeners = new Set<(s: SessionDegradedState) => void>();

/**
 * Dispatched on `window` when a verification proves the session healthy.
 * Shared subscriptions that are down after an error listen for it and
 * reconnect at once instead of waiting out their backoff.
 */
export const SESSION_HEALTHY_EVENT = 'twb:session-healthy';

// After a verification finishes, denials that land within this window
// are the tail of the same episode (sibling listeners, backoff retries
// that fired before the fresh token was in hand) — not new evidence.
const VERIFY_SETTLE_MS = 15 * 1000;

// While the banner is up for a refused read, look again on this schedule
// so a session that comes good takes the banner down by itself.
const RECHECK_DELAYS_MS = [30 * 1000, 2 * 60 * 1000, 5 * 60 * 1000];

let verifying: Promise<void> | null = null;
let lastVerifiedAt: number | null = null;
let recheckTimer: ReturnType<typeof setTimeout> | null = null;
let recheckAttempt = 0;

const emit = (): void => {
  for (const cb of listeners) cb(state);
};

const cancelRecheck = (): void => {
  if (recheckTimer !== null) {
    clearTimeout(recheckTimer);
    recheckTimer = null;
  }
};

const scheduleRecheck = (): void => {
  cancelRecheck();
  if (recheckAttempt >= RECHECK_DELAYS_MS.length) return;
  const delay = RECHECK_DELAYS_MS[recheckAttempt];
  recheckAttempt++;
  recheckTimer = setTimeout(() => {
    recheckTimer = null;
    void recheckDegradedSession();
  }, delay);
};

const setDegraded = (reason: SessionDegradedReason): void => {
  if (state.degraded && state.reason === reason) return;
  state = { degraded: true, reason };
  emit();
};

const clearDegraded = (): void => {
  cancelRecheck();
  recheckAttempt = 0;
  lastVerifiedAt = null;
  if (!state.degraded) return;
  state = { degraded: false, reason: null };
  emit();
};

/** Current session-degraded state. */
export const getSessionDegraded = (): SessionDegradedState => state;

/**
 * Subscribe to session-degraded changes. The callback fires on every
 * transition (degraded on, reason change, degraded off) — not on
 * subscribe. Returns the unsubscribe fn.
 */
export const subscribeSessionDegraded = (
  cb: (s: SessionDegradedState) => void,
): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/* ------------------------------------------------------------------ */
/* Triggers                                                            */
/* ------------------------------------------------------------------ */

/**
 * True when `err` looks like a Firebase permission-denied. Duck-typed on
 * the `code` property so this stays a leaf util — FirebaseError,
 * FirestoreError and the plain objects our tests throw all match.
 */
export const isPermissionDenied = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  (err as { code?: unknown }).code === 'permission-denied';

/** Offline / unreachable — says nothing about the session either way. */
const isNetworkFailure = (err: unknown): boolean => {
  const code =
    typeof err === 'object' && err !== null
      ? (err as { code?: unknown }).code
      : undefined;
  return (
    code === 'auth/network-request-failed' ||
    code === 'unavailable' ||
    code === 'deadline-exceeded'
  );
};

/**
 * Find out whether the session is actually broken: re-mint the tokens,
 * then read the user's own doc from the server with them.
 *
 *   - read succeeds        → healthy. Drop a 'permission' banner if one
 *                            is up and tell the listeners to reconnect.
 *   - read refused         → the session really is unusable → banner.
 *   - offline / unreachable → inconclusive; change nothing.
 *
 * Concurrent callers share one in-flight check.
 */
const verifySession = (): Promise<void> => {
  if (verifying) return verifying;
  verifying = (async () => {
    try {
      const { auth, db } = await import('../config/firebase');
      const user = auth.currentUser;
      if (!user) {
        // Signed out (or mid sign-out, when listeners are torn down with
        // exactly this error). Not a degraded session — ProtectedRoute
        // sends them to the login screen.
        return;
      }
      try {
        await mintSessionTokens(false);
      } catch (err) {
        if (isNetworkFailure(err)) {
          logger.debug('[appCheckRecovery] offline during verification');
          return;
        }
        logger.error('[appCheckRecovery] token refresh failed', err);
        setDegraded('permission');
        scheduleRecheck();
        return;
      }
      try {
        const { doc, getDocFromServer } = await import('firebase/firestore');
        await getDocFromServer(doc(db, 'users', user.uid));
      } catch (err) {
        if (isPermissionDenied(err)) {
          logger.error('[appCheckRecovery] fresh-token read refused', err);
          setDegraded('permission');
          scheduleRecheck();
        } else {
          logger.debug('[appCheckRecovery] verification inconclusive', err);
        }
        return;
      }
      logger.debug('[appCheckRecovery] session verified healthy');
      if (state.reason !== 'throttle') clearDegraded();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SESSION_HEALTHY_EVENT));
      }
    } catch (err) {
      // Never let a verification bug surface as an unhandled rejection.
      logger.warn('[appCheckRecovery] verification failed', err);
    } finally {
      // Stamped AFTER a healthy clear (which resets it), so the settle
      // window covers the stragglers from this episode either way.
      lastVerifiedAt = Date.now();
      verifying = null;
    }
  })();
  return verifying;
};

/**
 * Re-run the verification while the 'permission' banner is up, so it can
 * come down without a reload. No-op when the session isn't degraded for
 * that reason (an App Check throttle can only be cleared by reloading).
 */
export const recheckDegradedSession = (): Promise<void> => {
  if (!state.degraded || state.reason !== 'permission') return Promise.resolve();
  return verifySession();
};

/**
 * Force-refresh the session's tokens RIGHT NOW and report whether it
 * worked.
 *
 * The verification behind `handleFirestorePermissionDenied` is
 * fire-and-forget: it heals the listeners, which climb their own retry
 * ladder. A rejected WRITE has no ladder — the
 * caller is holding a failed commit and needs to know, synchronously
 * enough to retry it, whether a fresh token exists. Hence a second
 * entry point rather than a flag on the first.
 *
 * Both tokens get forced, because either one can be the stale half: the
 * ID token expires hourly, and App Check's auto-refresh is a timer that
 * a phone waking from sleep has just missed. A failure to mint an App
 * Check token is NOT fatal here — the ID token alone is often enough to
 * get the write through — so it's logged and the result still depends
 * on the auth refresh.
 *
 * Never throws, and never touches the banner — whether the session is
 * actually broken is `handleFirestorePermissionDenied`'s call.
 */
export const refreshSessionNow = async (): Promise<boolean> => {
  try {
    const refreshed = await mintSessionTokens(true);
    if (refreshed) logger.debug('[appCheckRecovery] session refreshed on demand');
    return refreshed;
  } catch (err) {
    logger.error('[appCheckRecovery] on-demand session refresh failed', err);
    return false;
  }
};

/**
 * Re-mint the ID token and bring the App Check token up to date. Returns
 * false when nobody is signed in; throws when the ID-token refresh
 * rejects. A failed App Check half is logged, never thrown.
 *
 * `forceAppCheck` decides how hard the App Check half pushes: `true`
 * always calls reCAPTCHA (the write-retry path, which is holding a
 * commit the server just refused); `false` returns the cached token
 * while it is still valid and only calls reCAPTCHA once it has actually
 * expired.
 */
const mintSessionTokens = async (forceAppCheck: boolean): Promise<boolean> => {
  // Dynamic import keeps this module a leaf (see the file header).
  const { auth, appCheck } = await import('../config/firebase');
  const user = auth.currentUser;
  if (!user) return false;
  await user.getIdToken(true);
  if (appCheck) {
    try {
      const { getToken } = await import('firebase/app-check');
      await getToken(appCheck, forceAppCheck);
    } catch (err) {
      // reCAPTCHA can decline or throttle; the auth refresh may still
      // be all the next request needed.
      logger.warn('[appCheckRecovery] App Check refresh failed', err);
    }
  }
  return true;
};

/**
 * Proactive refresh for the moment the app comes back to the foreground
 * (see `sessionKeepAlive`). Nothing has failed yet, so unlike
 * `refreshSessionNow` this:
 *
 *   - never touches the banner;
 *   - does NOT force App Check — forcing on every app-switch would burn
 *     reCAPTCHA quota and is itself the fastest way into the 24h throttle.
 *
 * Never throws.
 */
export const refreshSessionQuietly = async (): Promise<boolean> => {
  try {
    return await mintSessionTokens(false);
  } catch (err) {
    logger.warn('[appCheckRecovery] resume refresh failed', err);
    return false;
  }
};

/**
 * Public — Firestore subscription error handlers and write catch blocks
 * route permission-denied errors here. Time-gated (5-min grace), then one
 * verification per episode; the banner only goes up if that verification
 * finds the session genuinely refused.
 */
export const handleFirestorePermissionDenied = (): void => {
  const now = Date.now();
  if (now - pageLoadedAt < PERMISSION_DENIED_GRACE_MS) return;
  // Sibling listeners report the same denial within milliseconds — they
  // join the check already running rather than counting as new evidence.
  if (verifying) return;
  if (lastVerifiedAt !== null && now - lastVerifiedAt < VERIFY_SETTLE_MS) return;
  void verifySession();
};

/**
 * Public — called when the server has demonstrably accepted this
 * session: a shared subscription delivering data again after an error,
 * or a write being acknowledged. Authoritative "we're healthy", so it
 * drops the banner whatever raised it.
 */
export const notifyFirestoreRecovered = (): void => {
  clearDegraded();
};

/**
 * Wraps console.warn so we can catch the "@firebase/app-check: Requests
 * throttled due to 403 error" message it emits when the SDK gives up. No
 * public Firebase API exposes this signal directly.
 */
const installConsoleWarnInterceptor = (): void => {
  const original = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    original(...args);
    // Stringify defensively — args can be anything.
    const msg = args
      .map((a) => (typeof a === 'string' ? a : String(a)))
      .join(' ');
    if (isAppCheckThrottleMessage(msg)) {
      // A 24h client throttle can't be waited out or refreshed away —
      // degrade immediately, no self-heal attempt.
      setDegraded('throttle');
    }
  };
};

export const installAppCheckRecoveryHandler = (): void => {
  installConsoleWarnInterceptor();
};

/* ------------------------------------------------------------------ */
/* Write-path helper                                                   */
/* ------------------------------------------------------------------ */

/**
 * Resolve a user-facing message for a failed write, routing
 * permission-denied through the recovery flow on the way.
 *
 * Non-permission errors keep each call site's existing behavior exactly:
 * `err.message` (optionally reshaped by `formatMessage`, for sites that
 * wrap it in a prefix like `Import failed: …`), else `fallback`.
 */
export const describeWriteError = (
  err: unknown,
  fallback: string,
  formatMessage?: (message: string) => string,
): string => {
  if (isPermissionDenied(err)) {
    handleFirestorePermissionDenied();
    return SESSION_STALE_WRITE_MESSAGE;
  }
  if (err instanceof Error) {
    return formatMessage ? formatMessage(err.message) : err.message;
  }
  return fallback;
};
